import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CardsService } from './cards.service';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class CommonsService {
    constructor(private snackBar: MatSnackBar) {}
    getFileIcon(filename: string): string {
        const ext = filename.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf': return 'picture_as_pdf';
            case 'doc':
            case 'docx': return 'description';
            case 'xls':
            case 'xlsx': return 'table_chart';
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif': return 'image';
            case 'zip':
            case 'rar': return 'archive';
            case 'txt': return 'notes';
            default: return 'insert_drive_file';
        }
    }

    getDateFromDateTime(dateTime: string): string {
        if (dateTime) {
          // If it's a string, parse as local date
          let date = new Date(dateTime);
          // Adjust for timezone offset to get local date as YYYY-MM-DD
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        return '';
    }

    getTimeAgo(dateString: string): string {
        const now = new Date();
        const createdDate = new Date(dateString);
        const diffInMs = createdDate.getTime() - now.getTime(); // Changed order for future dates

        // Check if date is in the future
        if (diffInMs > 0) {
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
            const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

            if (diffInDays > 0) {
                const remainingHours = diffInHours % 24;
                const remainingMinutes = diffInMinutes % 60;

                if (diffInDays === 1 && remainingHours === 0 && remainingMinutes === 0) {
                    return '1 dzień';
                } else if (diffInDays === 1) {
                    return `1 dzień ${remainingHours > 0 ? remainingHours + ' godz. ' : ''}${remainingMinutes > 0 ? 'i ' + remainingMinutes + ' min.' : ''}`.trim();
                } else {
                    return `${diffInDays} dni ${remainingHours > 0 ? remainingHours + ' godz. ' : ''}${remainingMinutes > 0 ? 'i ' + remainingMinutes + ' min.' : ''}`.trim();
                }
            } else if (diffInHours > 0) {
                const remainingMinutes = diffInMinutes % 60;
                return `${diffInHours} godz. ${remainingMinutes > 0 ? remainingMinutes + ' min.' : ''}`.trim();
            } else if (diffInMinutes > 0) {
                return `${diffInMinutes} min.`;
            } else {
                return 'Właśnie teraz';
            }
        }

        // Past dates (existing logic)
        const pastDiffInMs = now.getTime() - createdDate.getTime();
        const diffInMinutes = Math.floor(pastDiffInMs / (1000 * 60));
        const diffInHours = Math.floor(pastDiffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(pastDiffInMs / (1000 * 60 * 60 * 24));

        if (diffInDays > 0) {
            const remainingHours = diffInHours % 24;
            const remainingMinutes = diffInMinutes % 60;

            if (diffInDays === 1 && remainingHours === 0 && remainingMinutes === 0) {
                return '1 dzień temu';
            } else if (diffInDays === 1) {
                return `1 dzień ${remainingHours > 0 ? remainingHours + ' godz. ' : ''}${remainingMinutes > 0 ? remainingMinutes + ' min. ' : ''}temu`.trim();
            } else {
                return `${diffInDays} dni ${remainingHours > 0 ? remainingHours + ' godz. ' : ''}${remainingMinutes > 0 ? remainingMinutes + ' min. ' : ''}temu`.trim();
            }
        } else if (diffInHours > 0) {
            const remainingMinutes = diffInMinutes % 60;
            return `${diffInHours} godz. ${remainingMinutes > 0 ? remainingMinutes + ' min. ' : ''}temu`.trim();
        } else if (diffInMinutes > 0) {
            return `${diffInMinutes} min. temu`;
        } else {
            return 'Właśnie teraz';
        }
    }

    /**
     * Generate a secure random password
     * @param length Password length (default: 12)
     * @returns Generated password string
     */
    generatePassword(length: number = 12): string {
        const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
        const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const lowerCase = 'abcdefghijklmnopqrstuvwxyz';
        const numbers = '0123456789';
        const specialChars = '!@#$%^&*';
        
        let password = '';
        
        // Ensure at least one of each type
        password += upperCase[Math.floor(Math.random() * upperCase.length)];
        password += lowerCase[Math.floor(Math.random() * lowerCase.length)];
        password += numbers[Math.floor(Math.random() * numbers.length)];
        password += specialChars[Math.floor(Math.random() * specialChars.length)];
        
        // Fill the rest randomly
        for (let i = password.length; i < length; i++) {
            password += charset[Math.floor(Math.random() * charset.length)];
        }
        
        // Shuffle the password to avoid predictable patterns
        password = password.split('').sort(() => Math.random() - 0.5).join('');
        
        return password;
    }

    /**
     * Copy text to clipboard and show notification
     * @param text Text to copy
     * @param successMessage Success notification message (default: 'Skopiowano do schowka')
     * @param errorMessage Error notification message (default: 'Nie udało się skopiować')
     */
    copyToClipboard(text: string, successMessage: string = 'Skopiowano do schowka', errorMessage: string = 'Nie udało się skopiować'): void {
        if (!text) return;

        navigator.clipboard.writeText(text).then(() => {
            this.snackBar.open(successMessage, 'Zamknij', {
                duration: 2000,
                panelClass: ['success-snackbar'],
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
            });
        }).catch(err => {
            console.error('Failed to copy to clipboard:', err);
            this.snackBar.open(errorMessage, 'Zamknij', {
                duration: 3000,
                panelClass: ['error-snackbar'],
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
            });
        });
    }

    /**
     * Download card PDF and change status to 'Printed'
     * @param cardsService CardsService instance
     * @param cardId Card ID
     * @param layoutId Layout ID for PDF generation
     * @param onSuccess Callback after successful status change
     * @param onError Optional callback on error
     */
    downloadCardPdf(
        cardsService: CardsService,
        cardId: number,
        layoutId: string,
        onSuccess: () => void,
        onError?: () => void
    ): void {
        cardsService.getCardPdf(cardId, layoutId).subscribe({
            next: (blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `karta_${cardId}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);

                cardsService.changeCardStatus(cardId, 3).subscribe({
                    next: () => {
                        onSuccess();
                        this.snackBar.open('Karta została wydrukowana. Status zmieniono na "Wydrukowana"', 'Zamknij', {
                            duration: 12000,
                            panelClass: ['success-snackbar'],
                            horizontalPosition: 'center',
                            verticalPosition: 'bottom',
                        });
                    },
                    error: () => {
                        if (onError) onError();
                        this.snackBar.open('Błąd podczas zapisu danych karty', 'Zamknij', {
                            duration: 4000,
                            panelClass: ['error-snackbar'],
                            horizontalPosition: 'center',
                            verticalPosition: 'bottom',
                        });
                    }
                });
            },
            error: () => {
                if (onError) onError();
                this.snackBar.open('Nie udało się pobrać PDF karty.', 'Zamknij', {
                    duration: 4000,
                    panelClass: ['error-snackbar']
                });
            }
        });
    }

    /**
     * Print card PDF and change status to 'Printed'
     * @param cardsService CardsService instance
     * @param cardId Card ID
     * @param onSuccess Callback after successful status change
     * @param onError Optional callback on error
     */
    printCardPdf(
        cardsService: CardsService,
        cardId: number,
        onSuccess: () => void,
        onError?: () => void
    ): void {
        cardsService.printCardPdf(cardId).subscribe({
            next: () => {
                this.snackBar.open('PDF karty został wysłany do druku. Drukarka oczekuje na wydruk.', 'Zamknij', {
                    duration: 12000,
                    panelClass: ['success-snackbar'],
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                });

                cardsService.changeCardStatus(cardId, 3).subscribe({
                    next: () => {
                        onSuccess();
                        this.snackBar.open('Status karty zmieniono na "Wydrukowana"', 'Zamknij', {
                            duration: 8000,
                            panelClass: ['success-snackbar'],
                            horizontalPosition: 'center',
                            verticalPosition: 'bottom',
                        });
                    },
                    error: () => {
                        if (onError) onError();
                        this.snackBar.open('Błąd podczas aktualizacji statusu karty', 'Zamknij', {
                            duration: 4000,
                            panelClass: ['error-snackbar'],
                            horizontalPosition: 'center',
                            verticalPosition: 'bottom',
                        });
                    }
                });
            },
            error: (error) => {
                if (onError) onError();
                const errorMessage = error?.error?.message || 'Błąd podczas wysyłania karty do druku';
                this.snackBar.open(errorMessage, 'Zamknij', {
                    duration: 5000,
                    panelClass: ['error-snackbar'],
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                });
            }
        });
    }

    /**
     * Attach click handlers to internal links in HTML content to use Angular router
     * @param contentElement HTML element containing links
     * @param router Angular Router instance
     */
    attachInternalLinkHandlers(contentElement: HTMLElement, router: Router): void {
        if (!contentElement) {
            return;
        }

        const links = contentElement.querySelectorAll('a');
        
        links.forEach((link: HTMLAnchorElement) => {
            const href = link.getAttribute('href');
            
            // Only handle internal links (not external, mailto, tel, etc.)
            if (href && 
                !href.startsWith('http://') && 
                !href.startsWith('https://') && 
                !href.startsWith('mailto:') && 
                !href.startsWith('tel:') &&
                !href.startsWith('#')) {
                
                link.addEventListener('click', (event: MouseEvent) => {
                    event.preventDefault();
                    event.stopPropagation();
                    
                    // Normalize the route
                    let route = href;
                    if (!route.startsWith('/')) {
                        route = '/' + route;
                    }
                    
                    router.navigate([route]);
                });
            }
        });
    }
}
